import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Button } from '../../components/ui/button';
import { Mail, Lock, Loader2, AlertCircle, Shield } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error('Invalid response from server');
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Invalid credentials');
      }

      // Store token and redirect to admin
      localStorage.setItem('admin_token', data.access_token);
      localStorage.setItem('admin_user', JSON.stringify(data.user));
      window.location.href = '/admin/subscribers';
    } catch (err) {
      setError(err.message || 'Login failed');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      <section className="pt-40 pb-20 px-6">
        <div className="max-w-md mx-auto">
          <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-8">
            {/* Admin Badge */}
            <div className="text-center mb-6">
              <span className="inline-flex items-center gap-2 bg-[#2563EB]/10 border border-[#2563EB]/30 rounded-full px-4 py-1.5 text-sm text-[#2563EB] font-medium">
                <Shield className="w-4 h-4" />
                Admin Access
              </span>
            </div>

            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-[#F8FAFC] mb-2">Admin Login</h1>
              <p className="text-[#94A3B8]">Access subscriber dashboard</p>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#F8FAFC] mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@example.com"
                    className="w-full bg-[#0A0F1C] border border-[#2563EB]/30 rounded-lg pl-12 pr-4 py-4 text-white placeholder-[#94A3B8] focus:outline-none focus:border-[#2563EB] transition-colors"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#F8FAFC] mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full bg-[#0A0F1C] border border-[#2563EB]/30 rounded-lg pl-12 pr-4 py-4 text-white placeholder-[#94A3B8] focus:outline-none focus:border-[#2563EB] transition-colors"
                    required
                  />
                </div>
              </div>

              <Button 
                type="submit"
                disabled={loading}
                className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white py-6 text-lg font-semibold rounded-lg"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            <p className="text-center text-[#94A3B8] text-sm mt-6">
              <Link to="/" className="text-[#2563EB] hover:underline">
                ← Back to Home
              </Link>
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LoginPage;
